import React from "react";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";
import FinalCTA from "@/components/FinalCTA";
import { CheckCircle2 } from "lucide-react";
import { useT } from "@/hooks/useT";

export default function Housekeeping() {
  const t = useT();
  const statuses = [
    ["101", t("E pastruar",     "Cleaned"),      "bg-[#DCFCE7] text-[#166534]"],
    ["102", t("Në pastrim",     "Cleaning"),     "bg-[#FEF3C7] text-[#92400E]"],
    ["103", t("Për pastrim",    "To clean"),     "bg-[#FEE2E2] text-[#991B1B]"],
    ["201", t("E pastruar",     "Cleaned"),      "bg-[#DCFCE7] text-[#166534]"],
    ["202", t("Jashtë shërbimi","Out of order"), "bg-[#E2E8F0] text-[#475569]"],
    ["301", t("Inspektuar",     "Inspected"),    "bg-[#E0F2FE] text-[#075985]"],
  ];
  const BULLETS = [
    t("Statuset: Për pastrim, Në pastrim, E pastruar, E inspektuar, Jashtë shërbimi", "Statuses: To clean, Cleaning, Cleaned, Inspected, Out of order"),
    t("Detyra ditore për secilën pastruese",                                              "A daily task list for each housekeeper"),
    t("Lidhje direkte me kalendarin e rezervimeve",                                        "Directly tied to the reservations calendar"),
    t("Njoftime për check-in më herët ose check-out të vonuar",                            "Alerts for early check-in or late check-out"),
    t("Raporte ditore për kohën dhe ngarkesën",                                            "Daily reports on time and workload"),
  ];
  return (
    <>
      <SEO
        title={t("Pastrimi i Dhomave — Housekeeping për Hotele", "Housekeeping — Room Cleaning for Hotels")}
        description={t(
          "Moduli i pastrimit në Akomoda: statusi i dhomave gjithmonë i përditësuar, detyrat e pastrueseve nga telefoni, dhe lidhja direkte me recepsionin.",
          "Akomoda's housekeeping module: always-updated room status, tasks on the housekeeper's phone, and a direct link with the front desk."
        )}
      />
      <PageHeader
        eyebrow={t("Pastrimi","Housekeeping")}
        title={t("Pastrimi i dhomave pa konfuzion","Room turnaround without the confusion")}
        description={t(
          "Pastrueset shohin nga telefoni vetëm dhomat që duhen përgatitur sot. Recepsioni e di menjëherë kur dhoma është gati për klientin tjetër.",
          "Housekeepers see only the rooms that need to be prepared today, on their phone. The front desk knows the second a room is ready for the next guest."
        )}
        ctas={[{ to: "/demo", label: t("Kërko Demo","Request Demo") }]}
        breadcrumb={[
          { label: t("Platforma","Platform"), to: "/platforma" },
          { label: t("Pastrimi","Housekeeping") },
        ]}
      />
      <section className="bg-surface-1">
        <div className="container-akm grid gap-10 py-14 md:grid-cols-12 md:py-20">
          <div className="md:col-span-5">
            <h2 className="h2-akm">{t("Pa kalim me letra. Pa harresa.","No paper trail. No forgotten rooms.")}</h2>
            <p className="lead mt-3">
              {t(
                "Çdo pastruese sheh listën e saj në telefon. Përditësimi i statusit bëhet me një prekje. Recepsioni e di menjëherë çfarë është gati dhe çfarë jo.",
                "Every housekeeper sees their own list on their phone. Status updates with one tap. The front desk instantly knows what's ready and what isn't."
              )}
            </p>
            <ul className="mt-6 space-y-2 text-sm text-ink">
              {BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#FF7A00]" /> {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-7">
            <div className="rounded-xl border border-line bg-white p-6 shadow-card">
              <div className="border-b border-line pb-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate2">{t("Statuset e dhomave — sot","Room status — today")}</div>
                <div className="font-display text-lg font-bold text-ink">{t("6 dhoma · 2 për pastrim · 1 jashtë shërbimi","6 rooms · 2 to clean · 1 out of order")}</div>
              </div>
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {statuses.map(([r, s, c]) => (
                  <div key={r} className="flex items-center justify-between rounded-md border border-line bg-bg px-3 py-2.5">
                    <span className="font-display text-sm font-bold text-ink">{t("Dhoma","Room")} {r}</span>
                    <span className={`rounded-md px-2 py-0.5 text-xs font-semibold ${c}`}>{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
